import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin, Phone, ArrowRight, ArrowUpRight, Check, Play, X, MessageCircle,
  Waves, Dumbbell, Building2, TreePalm, Baby, Flame, Film, Footprints,
  ShieldCheck, TrainFront, CalendarClock, BadgeCheck, Sparkles, ChefHat, Ruler, Home,
  FileSignature, HardHat, KeyRound,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { LeadForm } from "../components/LeadForm";
import { Seo, SITE_URL } from "../components/Seo";
import { CONTACT } from "../data/site";
import { useI18n } from "../i18n/LanguageContext";

const IMG = "/listone/img";

const UNITS = [
  { price: "540.000", eur: "132.000", size: "32 – 41 m²", ft: "347 – 437 ft²", img: `${IMG}/bedroom.jpg`,
    tours: [{ label: "Studio A1", url: "https://vr.justeasy.cn/view/1j76o7d92w6432m7-1770268135.html" }, { label: "Studio A2", url: "https://vr.justeasy.cn/view/317o67z692j77940-1770117244.html" }] },
  { price: "787.000", eur: "193.000", size: "64 – 67 m²", ft: "690 – 718 ft²", img: `${IMG}/living.jpg`,
    tours: [{ label: "1BHK B1", url: "https://vr.justeasy.cn/view/31876yc7h9268057-1770117396.html" }] },
  { price: "1.080.000", eur: "265.000", size: "93 – 112 m²", ft: "1.002 – 1.202 ft²", img: `${IMG}/dining.jpg`,
    tours: [{ label: "2BHK C3", url: "https://vr.justeasy.cn/view/1a7in6k3w7927509-1770117364.html" }, { label: "2BHD C1", url: "https://vr.justeasy.cn/view/176v28h792970o87-1770117380.html" }] },
];

const TOURS = [
  { label: "Lobby · Gym · Yoga", url: "https://vr.justeasy.cn/view/17v68693064353w2-1770268204.html", img: `${IMG}/lobby.jpg` },
  { label: "Studio A1", url: "https://vr.justeasy.cn/view/1j76o7d92w6432m7-1770268135.html", img: `${IMG}/bedroom.jpg` },
  { label: "Studio A2", url: "https://vr.justeasy.cn/view/317o67z692j77940-1770117244.html", img: `${IMG}/interior.jpg` },
  { label: "1BHK B1", url: "https://vr.justeasy.cn/view/31876yc7h9268057-1770117396.html", img: `${IMG}/living.jpg` },
  { label: "2BHK C3", url: "https://vr.justeasy.cn/view/1a7in6k3w7927509-1770117364.html", img: `${IMG}/dining.jpg` },
  { label: "2BHD C1", url: "https://vr.justeasy.cn/view/176v28h792970o87-1770117380.html", img: `${IMG}/bedroom2.jpg` },
];

const HIGHLIGHT_ICONS = [Sparkles, Home, ChefHat, Ruler];
const PAY_ICONS = [FileSignature, HardHat, KeyRound];
const AMENITY_ICONS = [Waves, Baby, Dumbbell, TreePalm, Building2, Footprints, Film, Flame];
const CONNECT_BIG = ["850 m", "3 min", "5 min", "10 min", "12 min", "15 min", "20 min", "20+"];

const COMPARE = [
  { name: "Listone Residence", price: "540K", m2: "11.600", plan: "20 / 40 / 40", handover: "Q2 2028", furnished: true, smart: true, metro: "850 m", best: true },
  { name: "Elevia Residences", price: "565K", m2: "12.400", plan: "50 / 50", handover: "Q4 2027", furnished: false, smart: false, metro: "~1,2 km" },
  { name: "Glorious Central", price: "538K", m2: "12.900", plan: "30 / 70", handover: "Q1 2027", furnished: false, smart: false, metro: "~1,5 km" },
  { name: "Moonsa Residences", price: "500K", m2: "15.600", plan: "60 / 40", handover: "ready", furnished: false, smart: false, metro: "~1 km" },
];

const GALLERY = [
  `${IMG}/exterior-facade.jpg`, `${IMG}/pool-sunset.jpg`, `${IMG}/entrance.jpg`,
  `${IMG}/exterior-sunset.jpg`, `${IMG}/pool-night.jpg`, `${IMG}/gym.jpg`,
  `${IMG}/yoga.jpg`, `${IMG}/court.jpg`, `${IMG}/kitchen.jpg`,
];

const CONTENT = {
  nl: {
    getInfo: "Ontvang informatie",
    heroEyebrow: "Nieuwe launch · Al Warsan, Dubai",
    heroSub: "Betaalbare luxe, slim ontworpen. Volledig gemeubileerde smart-home appartementen op 850 m van de metro — met een instapprijs die uniek is voor Dubai.",
    from: "vanaf",
    pill: ["Studio", "1 SLK", "2 SLK + Study"],
    ctaBrochure: "Ontvang de brochure & prijzen",
    ctaTours: "Bekijk 360° tours",
    trust: ["850 m tot metro", "Oplevering Q2 2028", "Escrow actief", "EOI vanaf AED 20.000"],
    introEyebrow: "Welkom bij Listone Residence",
    introTitle1: "Een familievriendelijke community in het hart van ",
    introAccent: "International City",
    introBody: "Ontwikkeld door Lion Stone Properties. Listone Residence combineert de rust van de natuur met verfijnd comfort — stromende architectuurlijnen, panoramische terrassen en doordachte voorzieningen. Rustig, betaalbaar en praktisch wonen, met parken, speeltuinen en alles binnen handbereik.",
    highlights: ["Volledig gemeubileerd", "Smart Home", "Branded keukenapparatuur", "Plafonds van 3,2 m"],
    galEyebrow: "Impressies",
    galTitle: "Ontworpen om van te houden",
    galSub: "Van de iconische lobby tot de infinity pool en resortvoorzieningen — elk detail ademt luxe.",
    unitsEyebrow: "Prijzen & units",
    unitsTitle: "Kies uw ideale woning",
    unitsSub: "Startprijzen bij launch. Beperkt aanbod — verzamel nu uw Expression of Interest vanaf slechts AED 20.000.",
    unitTypes: ["Studio", "1 Slaapkamer", "2 Slaapkamers + Study"],
    fromCap: "Vanaf",
    requestAvail: "Vraag beschikbaarheid aan",
    toursEyebrow: "Virtuele rondleiding",
    toursTitle: "Loop nu al door uw woning",
    toursSub: "Volledig interactieve 360° walkthroughs van elk unittype én de gedeelde voorzieningen.",
    amEyebrow: "Voorzieningen",
    amTitle: "Een resort voor de deur",
    amSub: "Meer dan alleen een woning — een levensstijl gericht op welzijn, sport en samenzijn.",
    amenities: ["Infinity pool", "Kinderzwembad", "Indoor gym", "Yoga studio", "Padel court", "Jogging track", "Outdoor cinema", "BBQ area"],
    locEyebrow: "Locatie · Al Warsan",
    locTitle: "Alles binnen handbereik",
    locSub: "Naast Dragon Mart, op 850 m van de metro en met uitstekende verbinding via E311 & E44.",
    connect: ["tot Metro Station", "Dragon Mart 1 & 2", "Fruit & Veg Market", "Dubai Safari Park", "Dubai Creek Harbour", "Dubai Int. Airport", "Downtown & Dubai Mall", "scholen in Al Warqa"],
    ctaEyebrow: "Launching next week",
    ctaTitle1: "Wees er als eerste bij — ",
    ctaAccent: "mis deze early opportunity niet",
    ctaBody: "Laat uw gegevens achter en ontvang de volledige brochure, prijslijst en betaalplan. Geen druk, geen aankoopcommissie — persoonlijk advies binnen 24 uur.",
    ctaList: ["Startprijs slechts AED 540.000", "EOI vanaf AED 20.000 — escrow-beschermd", "Oplevering Q2 2028", "Volledig gemeubileerd & smart home"],
    waBtn: "Direct via WhatsApp",
    footerBy: "door Lion Stone Properties",
    cmpEyebrow: "Vergelijking",
    cmpTitle: "Waarom Listone het slimste is",
    cmpSub: "Vergeleken met vergelijkbare off-plan projecten in Al Warsan / International City Phase 2 biedt Listone de laagste prijs per m², het vriendelijkste betaalplan én inbegrepen meubilair en smart home.",
    cmpCols: ["Project", "Vanaf", "Prijs/m²*", "Betaalplan", "Oplevering", "Gemeubileerd", "Smart home", "Metro"],
    cmpReady: "Opgeleverd",
    cmpBadge: "Beste keuze",
    cmpNote: "* Prijs per m² is indicatief, gebaseerd op openbare marktdata (Bayut & Property Finder, maart 2026). Prijzen van andere projecten kunnen variëren. Listone-cijfers volgens ontwikkelaar.",
    payEyebrow: "Betaalplan",
    payTitle: "Een helder 20 / 40 / 40 plan",
    paySub: "Instappen is eenvoudig en gespreid — reserveer met een kleine aanbetaling en betaal de rest tijdens de bouw en bij oplevering.",
    payEoi: "Start met een EOI van slechts AED 20.000",
    paySteps: [
      { pct: "20%", title: "Reservering", desc: "Bij ondertekening van het koopcontract (SPA)." },
      { pct: "40%", title: "Tijdens de bouw", desc: "Gespreid over meerdere bouwtermijnen." },
      { pct: "40%", title: "Bij oplevering", desc: "Restant bij sleuteloverdracht — Q2 2028." },
    ],
  },
  en: {
    getInfo: "Get information",
    heroEyebrow: "New launch · Al Warsan, Dubai",
    heroSub: "Affordable luxury, made smart. Fully furnished smart-home apartments just 850m from the metro — at an entry price that's unique for Dubai.",
    from: "from",
    pill: ["Studio", "1 BR", "2 BR + Study"],
    ctaBrochure: "Get the brochure & prices",
    ctaTours: "View 360° tours",
    trust: ["850m to metro", "Handover Q2 2028", "Escrow active", "EOI from AED 20,000"],
    introEyebrow: "Welcome to Listone Residence",
    introTitle1: "A family-oriented community in the heart of ",
    introAccent: "International City",
    introBody: "Developed by Lion Stone Properties. Listone Residence blends the serenity of nature with refined comfort — flowing architectural lines, panoramic terraces and thoughtfully curated amenities. Calm, affordable and practical living, with parks, playgrounds and everything within reach.",
    highlights: ["Fully furnished", "Smart Home", "Branded kitchen appliances", "3.2m high ceilings"],
    galEyebrow: "Impressions",
    galTitle: "Designed to be loved",
    galSub: "From the iconic lobby to the infinity pool and resort amenities — every detail speaks luxury.",
    unitsEyebrow: "Prices & units",
    unitsTitle: "Choose your ideal home",
    unitsSub: "Launch starting prices. Limited availability — secure your Expression of Interest now from just AED 20,000.",
    unitTypes: ["Studio", "1 Bedroom", "2 Bedrooms + Study"],
    fromCap: "From",
    requestAvail: "Request availability",
    toursEyebrow: "Virtual tour",
    toursTitle: "Walk through your home today",
    toursSub: "Fully interactive 360° walkthroughs of every unit type and the shared amenities.",
    amEyebrow: "Amenities",
    amTitle: "A resort at your doorstep",
    amSub: "More than just a home — a lifestyle built around wellness, sport and togetherness.",
    amenities: ["Infinity pool", "Kids' pool", "Indoor gym", "Yoga studio", "Padel court", "Jogging track", "Outdoor cinema", "BBQ area"],
    locEyebrow: "Location · Al Warsan",
    locTitle: "Everything within reach",
    locSub: "Next to Dragon Mart, 850m from the metro and excellent connectivity via E311 & E44.",
    connect: ["to Metro Station", "Dragon Mart 1 & 2", "Fruit & Veg Market", "Dubai Safari Park", "Dubai Creek Harbour", "Dubai Int. Airport", "Downtown & Dubai Mall", "schools in Al Warqa"],
    ctaEyebrow: "Launching next week",
    ctaTitle1: "Be the first — ",
    ctaAccent: "don't miss this early opportunity",
    ctaBody: "Leave your details and receive the full brochure, price list and payment plan. No pressure, no buyer commission — personal advice within 24 hours.",
    ctaList: ["Starting price just AED 540,000", "EOI from AED 20,000 — escrow protected", "Handover Q2 2028", "Fully furnished & smart home"],
    waBtn: "Chat on WhatsApp",
    footerBy: "by Lion Stone Properties",
    cmpEyebrow: "Comparison",
    cmpTitle: "Why Listone is the smart choice",
    cmpSub: "Compared to similar off-plan projects in Al Warsan / International City Phase 2, Listone offers the lowest price per m², the friendliest payment plan and includes furnishing and smart home.",
    cmpCols: ["Project", "From", "Price/m²*", "Payment plan", "Handover", "Furnished", "Smart home", "Metro"],
    cmpReady: "Ready",
    cmpBadge: "Best value",
    cmpNote: "* Price per m² is indicative, based on public market data (Bayut & Property Finder, March 2026). Other projects' prices may vary. Listone figures per developer.",
    payEyebrow: "Payment plan",
    payTitle: "A clear 20 / 40 / 40 plan",
    paySub: "Getting in is simple and staged — reserve with a small down payment and pay the rest during construction and at handover.",
    payEoi: "Start with an EOI of just AED 20,000",
    paySteps: [
      { pct: "20%", title: "Reservation", desc: "On signing the sales contract (SPA)." },
      { pct: "40%", title: "During construction", desc: "Spread across several build milestones." },
      { pct: "40%", title: "At handover", desc: "Balance on key handover — Q2 2028." },
    ],
  },
};

const fade = { hidden: { opacity: 0, y: 26 }, show: { opacity: 1, y: 0 } };

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
    {children}
  </motion.div>
);

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.22em] text-gold">{children}</span>
);

export default function ListoneLanding() {
  const { lang, setLang } = useI18n();
  const t = CONTENT[lang] || CONTENT.en;
  const [scrolled, setScrolled] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Residence", name: "Listone Residence",
    description: "Off-plan apartments in Al Warsan, Dubai. Studios, 1 and 2 bedrooms with smart home, fully furnished. From AED 540,000.",
    url: `${SITE_URL}/listone`, image: `${SITE_URL}${IMG}/exterior-facade.jpg`,
    address: { "@type": "PostalAddress", addressLocality: "Al Warsan, Dubai", addressCountry: "AE" },
  };

  const langBtn = (l) => `rounded-full px-2.5 py-1 text-xs font-semibold transition ${lang === l ? "bg-gold text-white" : "text-white/70 hover:text-white"}`;

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="listone-landing">
      <Seo path="/listone"
        title={lang === "nl" ? "Listone Residence — Off-plan in Dubai vanaf AED 540K" : "Listone Residence — Off-plan in Dubai from AED 540K"}
        description={lang === "nl"
          ? "Investeer in Listone Residence, Al Warsan Dubai. Volledig gemeubileerde smart-home appartementen vanaf AED 540.000. 850m tot metro, oplevering Q2 2028, escrow actief. Bekijk de 360° tours."
          : "Invest in Listone Residence, Al Warsan Dubai. Fully furnished smart-home apartments from AED 540,000. 850m to metro, handover Q2 2028, escrow active. View the 360° tours."}
        image={`${IMG}/exterior-facade.jpg`} jsonLd={jsonLd} />

      {/* ===== Sticky header ===== */}
      <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${scrolled ? "bg-navy/95 backdrop-blur-md shadow-lg shadow-black/20" : "bg-transparent"}`} data-testid="listone-header">
        <div className="mx-auto flex max-w-[1360px] items-center justify-between px-5 py-4 sm:px-8">
          <Logo variant="light" />
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-0.5 rounded-full border border-white/20 p-0.5" data-testid="listone-lang">
              <button onClick={() => setLang("en")} data-testid="listone-lang-en" className={langBtn("en")}>EN</button>
              <button onClick={() => setLang("nl")} data-testid="listone-lang-nl" className={langBtn("nl")}>NL</button>
            </div>
            <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid="header-wa"
              className="hidden items-center gap-2 rounded-full bg-wa px-4 py-2 text-sm font-medium text-white transition hover:-translate-y-0.5 sm:inline-flex">
              <MessageCircle size={15} /> WhatsApp
            </a>
            <button onClick={scrollToForm} data-testid="header-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-white transition hover:-translate-y-0.5 hover:bg-gold-400">
              {t.getInfo} <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="relative flex min-h-screen items-center overflow-hidden">
        <video autoPlay muted loop playsInline poster={`${IMG}/exterior-sunset.jpg`}
          className="absolute inset-0 h-full w-full object-cover" data-testid="hero-video">
          <source src="/listone/listone-tour.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
        <div className="absolute inset-0 bg-navy/30" />

        <div className="relative z-10 mx-auto w-full max-w-[1360px] px-5 pb-20 pt-32 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <Eyebrow>{t.heroEyebrow}</Eyebrow>
            <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
              Listone <span className="text-gold-300">Residence</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">{t.heroSub}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {[["540K", "132K"], ["787K", "193K"], ["1,08M", "265K"]].map(([p, eur], i) => (
                <div key={i} className="rounded-2xl border border-white/15 bg-white/5 px-4 py-2.5 backdrop-blur-sm">
                  <span className="block text-[11px] uppercase tracking-wide text-white/50">{t.pill[i]} {t.from}</span>
                  <span className="font-display text-lg font-semibold text-gold-300">
                    AED {p}{lang === "nl" && <span className="text-sm font-normal text-white/55"> · ± €{eur}</span>}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <button onClick={scrollToForm} data-testid="hero-cta"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium text-white transition hover:-translate-y-0.5 hover:bg-gold-400 hover:shadow-gold">
                {t.ctaBrochure} <ArrowRight size={16} />
              </button>
              <a href="#tours" data-testid="hero-tours"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 text-sm font-medium text-white transition hover:bg-white/10">
                <Play size={15} /> {t.ctaTours}
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/70">
              {[TrainFront, CalendarClock, ShieldCheck, BadgeCheck].map((Ic, i) => (
                <span key={i} className="inline-flex items-center gap-2"><Ic size={16} className="text-gold-300" /> {t.trust[i]}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== Intro ===== */}
      <section className="bg-cream py-24 text-navy">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>{t.introEyebrow}</Eyebrow>
              <h2 className="mt-6 font-display text-4xl font-semibold leading-tight sm:text-5xl">
                {t.introTitle1}<span className="text-gold">{t.introAccent}</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted">{t.introBody}</p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {t.highlights.map((label, i) => {
                  const Ic = HIGHLIGHT_ICONS[i];
                  return (
                    <div key={label} className="flex items-center gap-3 rounded-2xl border border-navy/8 bg-white p-4 shadow-soft">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold"><Ic size={18} /></span>
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                  );
                })}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-[2rem] shadow-2xl">
                <img src={`${IMG}/exterior-building.jpg`} alt="Listone Residence" className="h-[520px] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== Gallery ===== */}
      <section className="bg-navy py-24">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.galEyebrow}</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-semibold sm:text-5xl">{t.galTitle}</h2>
            <p className="mt-4 text-white/70">{t.galSub}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-3">
            {GALLERY.map((src, i) => (
              <Reveal key={src} delay={(i % 3) * 0.08}>
                <button onClick={() => setLightbox(src)} data-testid={`gallery-${i}`}
                  className="group relative block aspect-[4/3] w-full overflow-hidden rounded-2xl">
                  <img src={src} alt="Listone Residence" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className="absolute inset-0 bg-navy/0 transition-colors duration-500 group-hover:bg-navy/20" />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Units & prices ===== */}
      <section className="bg-cream py-24 text-navy">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.unitsEyebrow}</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-semibold sm:text-5xl">{t.unitsTitle}</h2>
            <p className="mt-4 text-muted">{t.unitsSub}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {UNITS.map((u, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="flex h-full flex-col overflow-hidden rounded-[1.75rem] bg-white shadow-soft transition-transform duration-500 hover:-translate-y-1" data-testid={`unit-${i}`}>
                  <div className="relative aspect-[16/11] overflow-hidden">
                    <img src={u.img} alt={t.unitTypes[i]} className="h-full w-full object-cover" />
                    <span className="absolute left-4 top-4 rounded-full bg-navy/85 px-3 py-1 text-xs font-medium text-white">{t.unitTypes[i]}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-xs uppercase tracking-wide text-muted">{t.fromCap}</p>
                    <p className="font-display text-3xl font-semibold text-gold">AED {u.price}</p>
                    {lang === "nl" && <p className="mt-0.5 text-sm text-muted">± € {u.eur}</p>}
                    <div className="mt-3 flex items-center gap-4 text-sm text-muted">
                      <span className="inline-flex items-center gap-1.5"><Ruler size={15} className="text-gold" /> {u.size}</span>
                      <span>{u.ft}</span>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {u.tours.map((tr) => (
                        <a key={tr.url} href={tr.url} target="_blank" rel="noreferrer" data-testid={`unit-tour-${tr.label}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-3 py-1.5 text-xs font-medium text-navy transition hover:bg-navy hover:text-white">
                          360° {tr.label} <ArrowUpRight size={13} />
                        </a>
                      ))}
                    </div>
                    <button onClick={scrollToForm} className="btn-primary mt-6 w-full">{t.requestAvail}</button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Payment plan timeline ===== */}
      <section className="bg-cream py-24 text-navy">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <div className="flex justify-center"><Eyebrow>{t.payEyebrow}</Eyebrow></div>
            <h2 className="mt-6 font-display text-4xl font-semibold sm:text-5xl">{t.payTitle}</h2>
            <p className="mt-4 text-muted">{t.paySub}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 rounded-[2rem] bg-navy p-8 text-white shadow-2xl sm:p-14">
              <div className="relative grid gap-12 md:grid-cols-3 md:gap-6">
                {/* connecting line (desktop) */}
                <div className="absolute left-0 right-0 top-9 hidden h-px bg-gradient-to-r from-gold/10 via-gold/50 to-gold/10 md:block" />
                {t.paySteps.map((s, i) => {
                  const Ic = PAY_ICONS[i];
                  return (
                    <div key={i} className="relative flex flex-col items-center text-center" data-testid={`pay-step-${i}`}>
                      <span className="relative z-10 flex h-[72px] w-[72px] items-center justify-center rounded-full border border-gold/40 bg-navy shadow-[0_0_0_6px_rgba(198,145,46,0.08)]">
                        <Ic size={26} className="text-gold-300" />
                      </span>
                      <span className="mt-6 font-display text-5xl font-semibold text-gold-300">{s.pct}</span>
                      <span className="mt-3 font-display text-lg font-semibold">{s.title}</span>
                      <span className="mt-2 max-w-[15rem] text-sm leading-relaxed text-white/65">{s.desc}</span>
                    </div>
                  );
                })}
              </div>

              {/* progress bar */}
              <div className="mt-14">
                <div className="flex h-2.5 w-full overflow-hidden rounded-full">
                  <span className="h-full bg-gold-300" style={{ width: "20%" }} />
                  <span className="h-full bg-gold" style={{ width: "40%", opacity: 0.85 }} />
                  <span className="h-full bg-gold" style={{ width: "40%", opacity: 0.6 }} />
                </div>
                <div className="mt-6 flex justify-center">
                  <span className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-5 py-2.5 text-sm font-medium text-gold-300">
                    <BadgeCheck size={16} /> {t.payEoi}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 360 tours ===== */}
      <section id="tours" className="bg-navy py-24">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.toursEyebrow}</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-semibold sm:text-5xl">{t.toursTitle}</h2>
            <p className="mt-4 text-white/70">{t.toursSub}</p>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TOURS.map((tr, i) => (
              <Reveal key={tr.url} delay={(i % 3) * 0.08}>
                <a href={tr.url} target="_blank" rel="noreferrer" data-testid={`tour-${i}`}
                  className="group relative block aspect-[16/10] overflow-hidden rounded-2xl">
                  <img src={tr.img} alt={tr.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
                  <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-gold text-white transition-transform duration-500 group-hover:scale-110"><Play size={18} /></span>
                  <span className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                    <span className="font-display text-lg font-semibold">{tr.label}</span>
                    <ArrowUpRight size={20} className="text-gold-300" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Amenities ===== */}
      <section className="relative overflow-hidden bg-cream py-24 text-navy">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <Reveal>
              <Eyebrow>{t.amEyebrow}</Eyebrow>
              <h2 className="mt-6 font-display text-4xl font-semibold sm:text-5xl">{t.amTitle}</h2>
              <p className="mt-4 text-muted">{t.amSub}</p>
              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
                {t.amenities.map((label, i) => {
                  const Ic = AMENITY_ICONS[i];
                  return (
                    <div key={label} className="flex items-center gap-3 rounded-2xl border border-navy/8 bg-white p-4 shadow-soft">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold"><Ic size={18} /></span>
                      <span className="text-sm font-medium leading-tight">{label}</span>
                    </div>
                  );
                })}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-3">
                <img src={`${IMG}/pool-night.jpg`} alt="Infinity pool" className="col-span-2 h-56 w-full rounded-2xl object-cover" />
                <img src={`${IMG}/gym.jpg`} alt="Gym" className="h-44 w-full rounded-2xl object-cover" />
                <img src={`${IMG}/yoga.jpg`} alt="Yoga" className="h-44 w-full rounded-2xl object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== Location ===== */}
      <section className="bg-navy py-24">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.locEyebrow}</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-semibold sm:text-5xl">{t.locTitle}</h2>
            <p className="mt-4 text-white/70">{t.locSub}</p>
          </Reveal>
          <div className="mt-12 grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <Reveal>
              <div className="overflow-hidden rounded-[2rem] bg-white shadow-2xl">
                <img src="/listone/location-map.jpg" alt="Listone Residence location map" className="w-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-3">
                {CONNECT_BIG.map((big, i) => (
                  <div key={i} className="rounded-2xl border border-white/12 bg-white/5 p-5 backdrop-blur-sm">
                    <p className="font-display text-2xl font-semibold text-gold-300">{big}</p>
                    <p className="mt-1 text-sm text-white/70">{t.connect[i]}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== Comparison ===== */}
      <section className="bg-navy py-24">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t.cmpEyebrow}</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-semibold sm:text-5xl">{t.cmpTitle}</h2>
            <p className="mt-4 text-white/70">{t.cmpSub}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 overflow-x-auto rounded-3xl border border-white/10 bg-white/[0.03]">
              <table className="w-full min-w-[860px] border-collapse text-left" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-white/10">
                    {t.cmpCols.map((c, i) => (
                      <th key={c} className={`px-5 py-4 text-xs font-semibold uppercase tracking-wide text-white/50 ${i === 0 ? "" : "text-center"}`}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((row) => (
                    <tr key={row.name} data-testid={`compare-row-${row.best ? "listone" : row.name.split(" ")[0].toLowerCase()}`}
                      className={`border-b border-white/5 transition-colors ${row.best ? "bg-gold/10" : "hover:bg-white/[0.03]"}`}>
                      <td className="px-5 py-5">
                        <div className="flex items-center gap-2">
                          <span className={`font-display text-base font-semibold ${row.best ? "text-gold-300" : "text-white"}`}>{row.name}</span>
                          {row.best && <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">{t.cmpBadge}</span>}
                        </div>
                      </td>
                      <td className="px-5 py-5 text-center text-sm font-medium text-white">AED {row.price}</td>
                      <td className={`px-5 py-5 text-center text-sm font-semibold ${row.best ? "text-gold-300" : "text-white/80"}`}>AED {row.m2}</td>
                      <td className="px-5 py-5 text-center text-sm text-white/80">{row.plan}</td>
                      <td className="px-5 py-5 text-center text-sm text-white/80">{row.handover === "ready" ? t.cmpReady : row.handover}</td>
                      <td className="px-5 py-5 text-center">{row.furnished ? <Check size={18} className="mx-auto text-gold-300" /> : <X size={16} className="mx-auto text-white/25" />}</td>
                      <td className="px-5 py-5 text-center">{row.smart ? <Check size={18} className="mx-auto text-gold-300" /> : <X size={16} className="mx-auto text-white/25" />}</td>
                      <td className={`px-5 py-5 text-center text-sm ${row.best ? "font-semibold text-gold-300" : "text-white/80"}`}>{row.metro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-white/40">{t.cmpNote}</p>
          </Reveal>
        </div>
      </section>

      {/* ===== CTA + form ===== */}
      <section ref={formRef} className="bg-cream py-24 text-navy" data-testid="listone-form">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <Eyebrow>{t.ctaEyebrow}</Eyebrow>
              <h2 className="mt-6 font-display text-4xl font-semibold leading-tight sm:text-5xl">
                {t.ctaTitle1}<span className="text-gold">{t.ctaAccent}</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted">{t.ctaBody}</p>
              <div className="mt-8 space-y-3">
                {t.ctaList.map((li) => (
                  <p key={li} className="flex items-center gap-3 text-sm font-medium">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold/15 text-gold"><Check size={14} /></span>{li}
                  </p>
                ))}
              </div>
              <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid="form-wa" className="btn-wa mt-8">
                <Phone size={16} /> {t.waBtn}
              </a>
            </Reveal>
            <Reveal delay={0.12}>
              <LeadForm interest="Listone Residence" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t border-white/10 bg-navy py-10">
        <div className="mx-auto flex max-w-[1360px] flex-col items-center justify-between gap-4 px-5 sm:flex-row sm:px-8">
          <Logo variant="light" />
          <p className="text-sm text-white/50">© 2026 OffplanDubai.nl · Listone Residence {t.footerBy}</p>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <a href={CONTACT.emailHref} className="hover:text-gold-300">{CONTACT.email}</a>
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-gold-300" /> Al Warsan, Dubai</span>
          </div>
        </div>
      </footer>

      {/* ===== Lightbox ===== */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} data-testid="lightbox"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/95 p-6 backdrop-blur-sm">
          <button className="absolute right-6 top-6 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><X size={24} /></button>
          <img src={lightbox} alt="" className="max-h-[88vh] max-w-full rounded-2xl object-contain shadow-2xl" />
        </div>
      )}
    </div>
  );
}
